import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Typography,
  Chip,
  Box,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import MDButton from "components/MDButton";
import { useSelector, useDispatch } from "react-redux";
import { setMultiEditPopup, setRefresh } from "features/alert/alertSlice";
import { editAlertConfiguration } from "config/apis";
import { fetchAlertConfigDetails } from "features/alert/alertThunk";

export default function EditAlertDialog() {
  const dispatch = useDispatch();
  const multyEditPopup = useSelector((state) => state.alerts.multyEditPopup);
  const alertData = useSelector((state) => state.alerts.alertData);
  const selectedRows = useSelector((state) => state.alerts.selectedRows);
  const selectedAlert = useSelector((state) => state.alerts.selectedAlert);

  const ignitionAlertIds = ["14", "9", "6", "10", "7", "8"];

  const isIgnitionAlert = ignitionAlertIds.includes(
    String(selectedAlert?.alertId)
  );

  const knotsToKmh = (knots) => {
    if (knots === "" || knots === null || knots === undefined) return "";

    return Math.round(Number(knots) * 1.852);
  };

  const [formData, setFormData] = useState({
    alertValue: "",
    mobileNumbers: [],
    emails: [],
    startTime: "", // Default start time
    endTime: "",
  });

  const [mobileInput, setMobileInput] = useState("");
  const [emailInput, setEmailInput] = useState("");

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleClose = () => dispatch(setMultiEditPopup(false));

  const handleSnackbarClose = () =>
    setSnackbar({ ...snackbar, open: false });

  // ================= VALIDATION =================

  const isValidEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const isValidMobile = (mobile) => {
    const regex = /^[0-9]{10}$/;
    return regex.test(mobile);
  };

  // ================= EMAIL =================

  const handleAddEmail = () => {
    const trimmed = emailInput.trim();
    if (!trimmed) return;

    if (!isValidEmail(trimmed)) {
      setSnackbar({
        open: true,
        message: "Please enter a valid Email ID",
        severity: "error",
      });
      return;
    }

    if (formData.emails.includes(trimmed)) {
      setSnackbar({
        open: true,
        message: "Email already added",
        severity: "warning",
      });
      return;
    }

    setFormData({
      ...formData,
      emails: [...formData.emails, trimmed],
    });

    setEmailInput("");
  };

  const handleDeleteEmail = (email) => {
    setFormData({
      ...formData,
      emails: formData.emails.filter((e) => e !== email),
    });
  };

  // ================= MOBILE =================

  const handleAddMobile = () => {
    const trimmed = mobileInput.trim();
    if (!trimmed) return;

    if (!isValidMobile(trimmed)) {
      setSnackbar({
        open: true,
        message: "Mobile number must be 10 digits",
        severity: "error",
      });
      return;
    }

    if (formData.mobileNumbers.includes(trimmed)) {
      setSnackbar({
        open: true,
        message: "Mobile number already added",
        severity: "warning",
      });
      return;
    }

    setFormData({
      ...formData,
      mobileNumbers: [...formData.mobileNumbers, trimmed],
    });

    setMobileInput("");
  };

  const handleDeleteMobile = (num) => {
    setFormData({
      ...formData,
      mobileNumbers: formData.mobileNumbers.filter((m) => m !== num),
    });
  };

  // ================= SUBMIT =================

  const handleSubmit = async () => {
    if (!isIgnitionAlert && !formData.alertValue) {
      setSnackbar({
        open: true,
        message: "Alert Value is required",
        severity: "error",
      });
      return;
    }

    const { startTime, endTime } = formData;
    if ((startTime && !endTime) || (!startTime && endTime)) {
      setSnackbar({
        open: true,
        message: "Please provide both Start Time and End Time, or leave both empty.",
        severity: "error",
      });
      return;
    }

    const token = localStorage.getItem("token");

    try {
      // 🔥 Create bulk payload
      const payload = selectedRows
        .map((row) => {
          const alert = alertData.find(
            (item) => item.thingId === row.thingId
          );

          if (!alert) return null;

          return {
            thingId: alert.thingId,
            alertID: alert.alertId,
            alertValue: isIgnitionAlert
              ? ""
              : knotsToKmh(formData.alertValue),
            mobileNumber: formData.mobileNumbers,
            emailId: formData.emails,
            alertEnabled: true,
            alertValueOption: true,
            startTime: formData.startTime, // Added
            endTime: formData.endTime,
          };
        })
        .filter(Boolean);

      if (!payload.length) {
        throw new Error("No valid vehicles selected");
      }

      // 🚀 Single API Call
      const response = await fetch(editAlertConfiguration, {
        method: "POST",
        headers: {
          "x-dhundhoo-session": token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result?.responseCode === 200) {
        // 🔥 Extract individual success messages
        const successMessages =
          result?.responseData?.map(
            (item) => `${item.thingID} - ${item.message}`
          ) || [];

        setSnackbar({
          open: true,
          message:
            successMessages.length > 0
              ? successMessages.join(", ")
              : "Alert updated successfully",
          severity: "success",
        });

        setTimeout(() => {
          dispatch(fetchAlertConfigDetails(alert?.alertID));
        }, 3000);


        setFormData({
          alertValue: "",
          mobileNumbers: [],
          emails: [],
          startTime: "",
          endTime: "",
        });

        handleClose();
      } else {
        throw new Error(result?.message || "Update failed");
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to update alerts",
        severity: "error",
      });
    }
  };


  return (
    <>
      <Dialog
        open={multyEditPopup}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: { borderRadius: "16px", boxShadow: 8 },
        }}
      >
        {/* HEADER */}
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: "#3177e8",
          }}
        >
          <Typography variant="h6" color="white" fontWeight="bold">
            Edit Alert Configuration
          </Typography>
          <IconButton onClick={handleClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* BODY */}
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 2 }}>
          {!isIgnitionAlert && (
            <TextField
              label="Alert Value (knots)"
              fullWidth
              value={formData.alertValue}
              onChange={(e) =>
                setFormData({ ...formData, alertValue: e.target.value })
              }
            />
          )}

          {/* Mobile */}
          {/* <Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                label="Add Mobile Number"
                fullWidth
                value={mobileInput}
                onChange={(e) => setMobileInput(e.target.value)}
              />
              <IconButton
                onClick={handleAddMobile}
                sx={{
                  bgcolor: "#3177e8",
                  color: "white",
                  "&:hover": { bgcolor: "#255ec7" },
                }}
              >
                <AddIcon />
              </IconButton>
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {formData.mobileNumbers.map((num, i) => (
                <Chip
                  key={i}
                  label={num}
                  onDelete={() => handleDeleteMobile(num)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box> */}

          {/* Email */}
          <Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                label="Add Email ID"
                fullWidth
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
              />
              <IconButton
                onClick={handleAddEmail}
                sx={{
                  bgcolor: "#3177e8",
                  color: "white",
                  "&:hover": { bgcolor: "#255ec7" },
                }}
              >
                <AddIcon />
              </IconButton>
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {formData.emails.map((email, i) => (
                <Chip
                  key={i}
                  label={email}
                  onDelete={() => handleDeleteEmail(email)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Start Time"
              type="time"
              fullWidth
              value={formData.startTime}
              onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Time"
              type="time"
              fullWidth
              value={formData.endTime}
              onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>

        {/* FOOTER */}
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton variant="outlined" color="secondary" onClick={handleClose}>
            Cancel
          </MDButton>
          <MDButton variant="contained" color="info" onClick={handleSubmit}>
            Submit
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* SNACKBAR */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
